import "bootstrap/dist/css/bootstrap.min.css"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Alert, Drawer, Spin, message } from "antd"
import { Col, Form, Row } from "react-bootstrap"
import { useHistory, useParams } from "react-router-dom"
import "../Application/ApplicationInterview.css"
import "./styles.css"
import MobileHeaderV2 from "./MobileHeaderV2"
import PrequalLetterV2 from "./PrequalLetterV2"
import RatesV2 from "./RatesV2"
import AssetsV2 from "./AssetsV2"
import DeclarationStepV2 from "./DeclarationStepV2"
import EmploymentV2 from "./EmploymentV2"
import LiabilitiesV2 from "./LiabilitiesV2"
import LoanOfficerPanelV2 from "./LoanOfficerPanelV2"
import OtherIncomeV2 from "./OtherIncomeV2"
import OverviewV2 from "./OverviewV2"
import PropertyV2 from "./PropertyV2"
import RealEstateV2 from "./RealEstateV2"
import ResidenceV2 from "./ResidenceV2"
import SidebarV2 from "./SidebarV2"
import StepApplicationV2 from "./StepApplicationV2"
import YourInfoV2 from "./YourInfoV2"
import Documents from "../Documents/Documents"
import { Tasks } from "../Tasks"
import { addCoborrowerV2, deleteCoborrowerV2, getApplicationV2, notifyLoanOfficerApplicationFinishedV2, submitApplicationV2, updateParticipantAssetsV2, updateParticipantCreditCheckV2, updateParticipantDeclarationsV2, updateParticipantEmploymentV2, updateParticipantLiabilitiesV2, updateParticipantOtherIncomeV2, updateParticipantProfileV2, updateParticipantRealEstateV2, updateParticipantResidenceV2, updatePropertyV2 } from "./api/borrowerSideV2Client"
import { isRedirectingToLogoutOnUnauthorized, UNAUTHORIZED_SESSION_MESSAGE } from "./api/unauthorizedRedirect"
import {
    APPLICATION_V2_CONTINUE_SECTION,
    buildApplicationV2Path,
    canAuthorizeAndSubmitV2,
    readLegacyInterviewStepFromSession,
    resolveV2ResumeTarget,
} from "./resumePath"
import { hydrateReduxFromCurrentState } from "../Store"
import * as com from "../Common"
import { showPrequalLetterMenuV2 } from "./showPrequalLetterMenuV2"
import { track } from "../statsig"
import { PAGE, STATSIG } from "../statsig/events"

const applicationStatusRank = (status) => (
    com.CurrentStatus.hasOwnProperty(status) ? com.CurrentStatus[status] : 0
)

const justMarkedApplicationFinished = (previousStatus, nextStatus) => (
    applicationStatusRank(nextStatus) >= com.CurrentStatus.ApplicationFinished
    && applicationStatusRank(previousStatus) < com.CurrentStatus.ApplicationFinished
)

/** Avoid flashing raw backend/SQL text on the interview error pane. */
const toFriendlyApplicationLoadError = (rawMessage) => {
    const msg = String(rawMessage || "").trim()
    if (!msg) {
        return "Unable to load this application. Please try again."
    }
    if (/sql:\s*no rows|no rows in result set|unauthorized|please sign in again|no longer available/i.test(msg)) {
        return UNAUTHORIZED_SESSION_MESSAGE
    }
    return msg
}

const ApplicationInterviewV2 = () => {
    const { section, partyId } = useParams()
    const history = useHistory()
    const [application, setApplication] = useState(null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [addingCoborrower, setAddingCoborrower] = useState(false)
    const [deletingCoborrowerPartyId, setDeletingCoborrowerPartyId] = useState("")
    const [error, setError] = useState("")
    const [showLoanOfficerPanel, setShowLoanOfficerPanel] = useState(false)
    const [loanOfficer, setLoanOfficer] = useState(null)
    const [mobileStepMenuOpen, setMobileStepMenuOpen] = useState(false)

    const loadApplication = useCallback(async () => {
        setLoading(true)
        setError("")
        try {
            const body = await getApplicationV2()
            setApplication(body.Application)
            // Keep legacy Redux modules (Documents / Details) usable without
            // writing back through /auth/updatestate (still blocked on V2 routes).
            hydrateReduxFromCurrentState()
        } catch (err) {
            // 401: show sign-in prompt, then logout redirect follows shortly.
            if (isRedirectingToLogoutOnUnauthorized()) {
                setError(UNAUTHORIZED_SESSION_MESSAGE)
                setLoading(false)
                return
            }
            setError(toFriendlyApplicationLoadError(err?.message))
        } finally {
            if (!isRedirectingToLogoutOnUnauthorized()) {
                setLoading(false)
            }
        }
    }, [])

    const loadLoanOfficer = useCallback(async () => {
        try {
            const token = sessionStorage.getItem("ZeitroA")
            const response = await fetch("/borrower/getloanofficerinfo", {
                method: "GET",
                headers: { Authorization: `Bearer ${token}` },
            })
            if (response.status !== 200) {
                return
            }
            const data = await response.json()
            setLoanOfficer(data.Profile || null)
        } catch (err) {
            console.error("Error fetching loan officer info:", err)
        }
    }, [])

    useEffect(() => {
        loadApplication()
        loadLoanOfficer()
    }, [loadApplication, loadLoanOfficer])

    // Align with LOS：进贷款工作台打一次 PAGE_VIEW（value=loan）。
    // continue → yourinfo 是同组件 replace，不会重复打。
    useEffect(() => {
        track(STATSIG.COMMON.VIEW, PAGE.LOAN, { page: PAGE.LOAN })
    }, [])

    const toggleLoanOfficerPanel = useCallback((visible) => {
        setShowLoanOfficerPanel(typeof visible === "boolean" ? visible : (prev) => !prev)
    }, [])

    const activePartyId = useMemo(() => (
        partyId || application?.activePartyId || application?.primaryPartyId || ""
    ), [application, partyId])

    const activeParticipant = application?.participantsById?.[activePartyId]
    const primaryParticipant = application?.participantsById?.[application?.primaryPartyId]
    const activeAssets = application?.finance?.assetsByPartyId?.[activePartyId]
    const activeRealEstate = application?.finance?.realEstateByPartyId?.[activePartyId]
    const activeLiabilities = application?.finance?.liabilitiesByPartyId?.[activePartyId]
    const primaryLiabilities = application?.finance?.liabilitiesByPartyId?.[application?.primaryPartyId]
    const activeDeclarations = application?.declarationsByPartyId?.[activePartyId]
    const isContinueEntry = section === APPLICATION_V2_CONTINUE_SECTION
    const activeSection = isContinueEntry ? "yourinfo" : (section || "yourinfo")
    const shouldShowStepApplication = Boolean(
        activePartyId
        && !["overview", "documents", "tasks", "rates", "prequalletter"].includes(activeSection)
    )

    const mobileStepTitle = useMemo(() => {
        const titleMap = {
            yourinfo: "Personal / Your info",
            residence: "Personal / Current residence",
            property: "Subject property",
            employment: "Income / Employment",
            otherincome: "Income / Other income",
            assets: "Finance / Assets",
            realestate: "Finance / Real estate",
            liabilities: "Finance / Liabilities",
            propertymoney: "Declarations / Property & money",
            finances: "Declarations / Finances",
            creditcheck: "Declarations / Credit check",
        }
        return titleMap[activeSection] || ""
    }, [activeSection])

    useEffect(() => {
        setMobileStepMenuOpen(false)
    }, [activeSection, activePartyId])

    useEffect(() => {
        if (!application || isContinueEntry) {
            return
        }
        if (activeSection === "prequalletter" && !showPrequalLetterMenuV2(application)) {
            history.replace("/application/v2/interview/rates")
        }
    }, [activeSection, application, history, isContinueEntry])

    useEffect(() => {
        if (!application || isContinueEntry) {
            return
        }
        // Refresh Redux mirror when opening leftover legacy-connected modules.
        if (!["rates", "prequalletter", "documents"].includes(activeSection)) {
            return
        }
        hydrateReduxFromCurrentState()
    }, [activeSection, application?.loanId, application?.updatedAt, isContinueEntry])

    useEffect(() => {
        if (!application || isContinueEntry) {
            return
        }
        const knownPartyId = partyId && application.participantsById?.[partyId] ? partyId : ""
        const fallbackPartyId = application.primaryPartyId || application.participantOrder?.[0] || ""
        const nextPartyId = knownPartyId || fallbackPartyId
        if (!nextPartyId) {
            return
        }
        // Leftover modules do not need a partyId segment (and it breaks pathname loan-id parsers).
        if (["overview", "documents", "tasks", "rates", "prequalletter"].includes(activeSection)) {
            if (partyId) {
                history.replace(`/application/v2/interview/${activeSection}`)
            }
            return
        }
        if (partyId !== nextPartyId) {
            history.replace(`/application/v2/interview/${activeSection}/${nextPartyId}`)
        }
    }, [activeSection, application, history, isContinueEntry, partyId])

    useEffect(() => {
        if (!application || !isContinueEntry) {
            return
        }
        const { partyId: resumePartyId, section: resumeSection } = resolveV2ResumeTarget({
            application,
            legacyInterviewStep: readLegacyInterviewStepFromSession(),
        })
        if (!resumePartyId && resumeSection !== "overview") {
            return
        }
        history.replace(buildApplicationV2Path(resumeSection, resumePartyId))
    }, [application, history, isContinueEntry])

    const handleAddCoborrower = async () => {
        setAddingCoborrower(true)
        try {
            const body = await addCoborrowerV2()
            setApplication(body.Application)
            const loanId = body.Application?.loanId || ""
            const borrowerCount = Array.isArray(body.Application?.participantOrder)
                ? body.Application.participantOrder.length
                : 0
            track(STATSIG.LOAN.ADD_CO_BORROWER_BUTTON, loanId, {
                loan_id: loanId,
                type: "insert",
                borrower_count: String(borrowerCount),
            })
            if (body.Participant?.partyId) {
                history.push(`/application/v2/interview/yourinfo/${body.Participant.partyId}`)
            }
            message.success("Co-borrower added.", 1)
        } catch (err) {
            message.error(err.message)
        } finally {
            setAddingCoborrower(false)
        }
    }

    const handleDeleteCoborrower = async (deletePartyId) => {
        if (!deletePartyId) {
            return
        }
        setDeletingCoborrowerPartyId(deletePartyId)
        try {
            const previousStatus = application?.currentStatus
            const body = await deleteCoborrowerV2(deletePartyId)
            setApplication(body.Application)
            const borrowerCount = Array.isArray(body.Application?.participantOrder)
                ? body.Application.participantOrder.length
                : 0
            track(STATSIG.LOAN.DELETE_CO_BORROWER_BUTTON, "delete", {
                type: "delete",
                party_id: deletePartyId,
                borrower_count: String(borrowerCount),
            })
            if (justMarkedApplicationFinished(previousStatus, body.Application?.currentStatus)) {
                try {
                    await notifyLoanOfficerApplicationFinishedV2()
                } catch (err) {
                    console.error("notify LO after application finished:", err)
                }
            }
            if (deletePartyId === activePartyId) {
                const nextPartyId = body.Application?.primaryPartyId || ""
                history.push(`/application/v2/interview/yourinfo/${nextPartyId}`)
            }
            message.success("Co-borrower deleted.", 1)
        } catch (err) {
            message.error(err.message)
        } finally {
            setDeletingCoborrowerPartyId("")
        }
    }

    const handleSaveProfile = async (profile) => {
        if (!activePartyId) {
            message.error("Missing participant. Please reload and try again.")
            return
        }
        if (!activeParticipant) {
            message.error("Borrower participant not found. Please reload and try again.")
            return
        }
        setSaving(true)
        try {
            const body = await updateParticipantProfileV2(activePartyId, profile)
            setApplication(body.Application)
            message.success("Saved.", 1)
            history.push(`/application/v2/interview/residence/${activePartyId}`)
        } catch (err) {
            message.error(err.message)
        } finally {
            setSaving(false)
        }
    }

    const handleSaveResidence = async (residence) => {
        if (!activePartyId) {
            return
        }
        setSaving(true)
        try {
            const body = await updateParticipantResidenceV2(activePartyId, residence)
            setApplication(body.Application)
            message.success("Saved.", 1)
            history.push(`/application/v2/interview/property/${activePartyId}`)
        } catch (err) {
            message.error(err.message)
        } finally {
            setSaving(false)
        }
    }

    const handleSaveProperty = async (propertyPayload) => {
        if (!activePartyId) {
            return
        }
        setSaving(true)
        try {
            const body = await updatePropertyV2(propertyPayload, activePartyId)
            setApplication(body.Application)
            message.success("Saved.", 1)
            history.push(`/application/v2/interview/employment/${activePartyId}`)
        } catch (err) {
            message.error(err.message)
        } finally {
            setSaving(false)
        }
    }

    const handleBackToProfile = () => {
        if (activePartyId) {
            history.push(`/application/v2/interview/yourinfo/${activePartyId}`)
        }
    }

    const handleBackToResidence = () => {
        if (activePartyId) {
            history.push(`/application/v2/interview/residence/${activePartyId}`)
        }
    }

    const handleBackToProperty = () => {
        if (activePartyId) {
            history.push(`/application/v2/interview/property/${activePartyId}`)
        }
    }

    const handleBackToEmployment = () => {
        if (activePartyId) {
            history.push(`/application/v2/interview/employment/${activePartyId}`)
        }
    }

    const handleBackToOtherIncome = () => {
        if (activePartyId) {
            history.push(`/application/v2/interview/otherincome/${activePartyId}`)
        }
    }

    const handleSaveEmployment = async (employment) => {
        if (!activePartyId) {
            return
        }
        setSaving(true)
        try {
            const body = await updateParticipantEmploymentV2(activePartyId, employment)
            setApplication(body.Application)
            message.success("Saved.", 1)
            history.push(`/application/v2/interview/otherincome/${activePartyId}`)
        } catch (err) {
            message.error(err.message)
        } finally {
            setSaving(false)
        }
    }

    // Modal Save persists the employment data without completing the step or
    // navigating away. The Employment page's Next action continues to use
    // handleSaveEmployment above, which keeps the existing completion flow.
    const handlePersistEmployment = async (employment) => {
        if (!activePartyId) {
            return false
        }
        setSaving(true)
        try {
            const body = await updateParticipantEmploymentV2(activePartyId, {
                ...employment,
                markCompleted: false,
            })
            setApplication(body.Application)
            message.success("Saved.", 1)
            return true
        } catch (err) {
            message.error(err.message)
            return false
        } finally {
            setSaving(false)
        }
    }

    const handleSaveOtherIncome = async (otherIncome) => {
        if (!activePartyId) {
            return
        }
        setSaving(true)
        try {
            const body = await updateParticipantOtherIncomeV2(activePartyId, otherIncome)
            setApplication(body.Application)
            message.success("Saved.", 1)
            history.push(`/application/v2/interview/assets/${activePartyId}`)
        } catch (err) {
            message.error(err.message)
        } finally {
            setSaving(false)
        }
    }

    // Modal Save persists the Other income data without completing the step or
    // navigating away. The page's Next action keeps the existing completion flow.
    const handlePersistOtherIncome = async (otherIncome) => {
        if (!activePartyId) {
            return false
        }
        setSaving(true)
        try {
            const body = await updateParticipantOtherIncomeV2(activePartyId, {
                ...otherIncome,
                markCompleted: false,
            })
            setApplication(body.Application)
            message.success("Saved.", 1)
            return true
        } catch (err) {
            message.error(err.message)
            return false
        } finally {
            setSaving(false)
        }
    }

    const handleSaveAssets = async (assets) => {
        if (!activePartyId) {
            return
        }
        setSaving(true)
        try {
            const body = await updateParticipantAssetsV2(activePartyId, assets)
            setApplication(body.Application)
            message.success("Saved.", 1)
            history.push(`/application/v2/interview/realestate/${activePartyId}`)
        } catch (err) {
            message.error(err.message)
        } finally {
            setSaving(false)
        }
    }

    const handleSaveRealEstate = async (realEstatePayload) => {
        if (!activePartyId) {
            return
        }
        const realEstate = realEstatePayload?.realEstate || realEstatePayload
        setSaving(true)
        try {
            if (realEstatePayload?.residencePayload) {
                await updateParticipantResidenceV2(activePartyId, realEstatePayload.residencePayload)
            }
            if (realEstatePayload?.propertyPayload) {
                await updatePropertyV2(realEstatePayload.propertyPayload, activePartyId)
            }
            const body = await updateParticipantRealEstateV2(activePartyId, realEstate)
            setApplication(body.Application)
            message.success("Saved.", 1)
            history.push(`/application/v2/interview/liabilities/${activePartyId}`)
        } catch (err) {
            message.error(err.message)
        } finally {
            setSaving(false)
        }
    }

    // Modal Save persists the edited real-estate data without completing the
    // residence, property, or real-estate steps. Next keeps the existing flow.
    const handlePersistRealEstate = async (realEstatePayload) => {
        if (!activePartyId) {
            return false
        }
        const realEstate = realEstatePayload?.realEstate || realEstatePayload
        setSaving(true)
        try {
            if (realEstatePayload?.residencePayload) {
                await updateParticipantResidenceV2(activePartyId, {
                    ...realEstatePayload.residencePayload,
                    markCompleted: false,
                })
            }
            if (realEstatePayload?.propertyPayload) {
                await updatePropertyV2({
                    ...realEstatePayload.propertyPayload,
                    markCompleted: false,
                }, activePartyId)
            }
            const body = await updateParticipantRealEstateV2(activePartyId, realEstate, { markCompleted: false })
            setApplication(body.Application)
            message.success("Saved.", 1)
            return true
        } catch (err) {
            message.error(err.message)
            return false
        } finally {
            setSaving(false)
        }
    }

    const handleSaveLiabilities = async (liabilitiesPayload) => {
        if (!activePartyId) {
            return
        }
        const liabilities = liabilitiesPayload?.liabilities || liabilitiesPayload
        setSaving(true)
        try {
            if (liabilitiesPayload?.propertyPayload) {
                await updatePropertyV2(liabilitiesPayload.propertyPayload, activePartyId)
            }
            const body = await updateParticipantLiabilitiesV2(activePartyId, liabilities)
            setApplication(body.Application)
            message.success("Saved.", 1)
            history.push(`/application/v2/interview/propertymoney/${activePartyId}`)
        } catch (err) {
            message.error(err.message)
        } finally {
            setSaving(false)
        }
    }

    const handleSaveDeclarationStep = async ({ step, declarations, creditCheck, stayOnStep }) => {
        if (!activePartyId) {
            return
        }
        setSaving(true)
        try {
            // Credit-check Next also PATCHes liabilities (estimated debt); that is an
            // implementation detail — only the credit-check PATCH should count as one UPDATE.
            if (step === "creditcheck" && creditCheck?.liabilities) {
                await updateParticipantLiabilitiesV2(activePartyId, creditCheck.liabilities, {
                    skipStatsigTrack: true,
                })
            }
            const body = step === "creditcheck"
                ? await updateParticipantCreditCheckV2(activePartyId, {
                    socialSecurity: creditCheck.socialSecurity,
                    estimatedFico: Number(creditCheck.estimatedFico) || 0,
                    estimatedMonthlyDebt: creditCheck.estimatedMonthlyDebt,
                    creditconsented: creditCheck.creditconsented,
                    creditWarnings: creditCheck.creditWarnings,
                    complete: creditCheck.complete === true,
                })
                : await updateParticipantDeclarationsV2(activePartyId, declarations, step)
            let nextApplication = body.Application
            if (step === "creditcheck" && creditCheck.complete === true && canAuthorizeAndSubmitV2(application, activePartyId)) {
                const submitted = await submitApplicationV2(activePartyId)
                if (submitted.Application) {
                    nextApplication = submitted.Application
                }
                const loanId = nextApplication?.loanId || application?.loanId || ""
                track(STATSIG.LOAN.SUBMIT, loanId, {
                    loan_id: loanId,
                    party_id: activePartyId,
                    type: creditCheck?.automatedCreditPull
                        ? "authorize_and_submit"
                        : "submit",
                })
            }
            setApplication(nextApplication)
            if (stayOnStep || creditCheck?.complete === false) {
                return
            }
            if (
                step === "creditcheck"
                && justMarkedApplicationFinished(application?.currentStatus, nextApplication?.currentStatus)
            ) {
                try {
                    await notifyLoanOfficerApplicationFinishedV2()
                } catch (err) {
                    console.error("notify LO after application finished:", err)
                }
            }
            message.success("Saved.", 1)
            const nextByStep = {
                propertymoney: "finances",
                finances: "creditcheck",
            }
            if (step === "creditcheck") {
                const resume = resolveV2ResumeTarget({ application: nextApplication })
                history.push(resume.section === "overview"
                    ? "/application/v2/interview/overview"
                    : buildApplicationV2Path(resume.section, resume.partyId))
            } else {
                history.push(`/application/v2/interview/${nextByStep[step] || "creditcheck"}/${activePartyId}`)
            }
        } catch (err) {
            message.error(err.message)
        } finally {
            setSaving(false)
        }
    }

    const renderPane = () => {
        if (activeSection === "overview") {
            return <OverviewV2 application={application} />
        }

        if (activeSection === "documents") {
            return <Documents />
        }

        if (activeSection === "tasks") {
            const participants = (application?.participantOrder || []).map((id) => application?.participantsById?.[id]).filter(Boolean)
            return (
                <Tasks
                    application={{ borrower: {}, coborrower: {} }}
                    participants={participants}
                    updateChangeProgressType={() => {}}
                    updateChangeProgressLastVisited={() => {}}
                />
            )
        }

        if (activeSection === "rates") {
            return (
                <RatesV2
                    application={application}
                    onApplicationUpdated={setApplication}
                />
            )
        }

        if (activeSection === "prequalletter") {
            if (!showPrequalLetterMenuV2(application)) {
                return null
            }
            return (
                <PrequalLetterV2
                    loanId={application?.loanId || ""}
                    propertyType={application?.property?.propertytype || ""}
                    onApplicationUpdated={setApplication}
                />
            )
        }

        if (["propertymoney", "finances", "creditcheck"].includes(activeSection)) {
            const backByStep = {
                propertymoney: "liabilities",
                finances: "propertymoney",
                creditcheck: "finances",
            }
            return (
                <DeclarationStepV2
                    step={activeSection}
                    application={application}
                    participant={activeParticipant}
                    declarations={activeDeclarations}
                    liabilities={activeLiabilities}
                    saving={saving}
                    onSave={handleSaveDeclarationStep}
                    onBack={() => history.push(`/application/v2/interview/${backByStep[activeSection]}/${activePartyId}`)}
                />
            )
        }

        if (activeSection === "liabilities") {
            return (
                <LiabilitiesV2
                    participant={activeParticipant}
                    liabilities={activeLiabilities}
                    saving={saving}
                    onSave={handleSaveLiabilities}
                    onBack={() => history.push(`/application/v2/interview/realestate/${activePartyId}`)}
                />
            )
        }

        if (activeSection === "realestate") {
            return (
                <RealEstateV2
                    application={application}
                    participant={activeParticipant}
                    realEstate={activeRealEstate}
                    saving={saving}
                    onSave={handleSaveRealEstate}
                    onPersist={handlePersistRealEstate}
                    onBack={() => history.push(`/application/v2/interview/assets/${activePartyId}`)}
                />
            )
        }

        if (activeSection === "assets") {
            return (
                <AssetsV2
                    application={application}
                    assets={activeAssets}
                    participant={activeParticipant}
                    saving={saving}
                    onSave={handleSaveAssets}
                    onBack={handleBackToOtherIncome}
                />
            )
        }

        if (activeSection === "otherincome") {
            return (
                <OtherIncomeV2
                    application={application}
                    participant={activeParticipant}
                    saving={saving}
                    onSave={handleSaveOtherIncome}
                    onPersist={handlePersistOtherIncome}
                    onBack={handleBackToEmployment}
                />
            )
        }

        if (activeSection === "employment") {
            return (
                <EmploymentV2
                    application={application}
                    participant={activeParticipant}
                    saving={saving}
                    onSave={handleSaveEmployment}
                    onPersist={handlePersistEmployment}
                    onBack={handleBackToProperty}
                    onContactLoanOfficer={() => toggleLoanOfficerPanel(true)}
                />
            )
        }

        if (activeSection === "residence") {
            return (
                <ResidenceV2
                    application={application}
                    participant={activeParticipant}
                    primaryParticipant={primaryParticipant}
                    saving={saving}
                    onSave={handleSaveResidence}
                    onBack={handleBackToProfile}
                />
            )
        }

        if (activeSection === "property") {
            return (
                <PropertyV2
                    application={application}
                    primaryParticipant={primaryParticipant}
                    liabilities={primaryLiabilities}
                    saving={saving}
                    onSave={handleSaveProperty}
                    onBack={handleBackToResidence}
                />
            )
        }

        return (
            <YourInfoV2
                application={application}
                participant={activeParticipant}
                saving={saving}
                onSave={handleSaveProfile}
            />
        )
    }

    const currentPane = error ? (
        <div className="application-v2-error">
            <Alert
                type="error"
                showIcon
                message={error === UNAUTHORIZED_SESSION_MESSAGE ? UNAUTHORIZED_SESSION_MESSAGE : "Unable to load application"}
                description={error === UNAUTHORIZED_SESSION_MESSAGE ? undefined : error}
            />
        </div>
    ) : renderPane()

    return (
        <Spin className="application-loding" spinning={loading || (isContinueEntry && !error)}>
            <div className="d-flex" style={{ height: "100vh" }}>
                <div className="application-v2-mobile-header">
                    <MobileHeaderV2
                        application={application}
                        activePartyId={activePartyId}
                        activeSection={activeSection}
                        onAddCoborrower={handleAddCoborrower}
                        onDeleteCoborrower={handleDeleteCoborrower}
                        addingCoborrower={addingCoborrower}
                        deletingCoborrowerPartyId={deletingCoborrowerPartyId}
                    />
                </div>

                {shouldShowStepApplication && mobileStepTitle && (
                    <div className="application-v2-mobile-stepbar">
                        <div className="application-v2-mobile-stepbar-text">{mobileStepTitle}</div>
                        <button
                            type="button"
                            className="application-v2-mobile-stepbar-btn"
                            onClick={() => setMobileStepMenuOpen((prev) => !prev)}
                            aria-label="Open step menu"
                        >
                            <img
                                className={mobileStepMenuOpen ? "icon-expand" : "icon-collapse"}
                                src="/images/step-application-arrow.svg"
                                alt=""
                            />
                        </button>
                    </div>
                )}

                {shouldShowStepApplication && (
                    <Drawer
                        placement="top"
                        rootClassName="menu-drawer stepApplication-bar-drawer"
                        height="auto"
                        closable={false}
                        zIndex={999}
                        onClose={() => setMobileStepMenuOpen(false)}
                        open={mobileStepMenuOpen}
                    >
                        <StepApplicationV2
                            activePartyId={activePartyId}
                            activeSection={activeSection}
                            completedSteps={activeParticipant?.completedSteps || {}}
                        />
                    </Drawer>
                )}

                <SidebarV2
                    application={application}
                    activePartyId={activePartyId}
                    activeSection={activeSection}
                    onAddCoborrower={handleAddCoborrower}
                    onDeleteCoborrower={handleDeleteCoborrower}
                    addingCoborrower={addingCoborrower}
                    deletingCoborrowerPartyId={deletingCoborrowerPartyId}
                />
                <div
                    className={`prequalInterviewPane newInterviewPane application-v2-interview ${shouldShowStepApplication && mobileStepTitle ? "application-v2-interview-with-stepbar" : ""}`}
                    style={{
                        margin: 0,
                        borderRadius: 0,
                        border: "none",
                        position: "relative",
                    }}
                >
                    <div id="envelope" className="text-left mt-0 full-height" style={{ padding: 0, whiteSpace: "normal" }}>
                        <Form style={{ padding: 0 }} className="full-height" autoComplete="off" id="personalform" onSubmit={(e) => e.preventDefault()}>
                            <fieldset className="full-height" id="scrollBox" disabled={false}>
                                <Row className="ml-0 padding application-pane-container">
                                    <Col className="text-left m-0 px-0">
                                        {currentPane}
                                    </Col>
                                </Row>
                            </fieldset>
                        </Form>
                    </div>
                </div>
                {window.innerWidth >= 768 && shouldShowStepApplication && (
                    <div>
                        <StepApplicationV2
                            activePartyId={activePartyId}
                            activeSection={activeSection}
                            completedSteps={activeParticipant?.completedSteps || {}}
                        />
                    </div>
                )}
                <LoanOfficerPanelV2
                    show={showLoanOfficerPanel}
                    officer={loanOfficer}
                    onToggle={toggleLoanOfficerPanel}
                />
            </div>
        </Spin>
    )
}

export default ApplicationInterviewV2
